import Lenis from 'lenis';
import { useEffect, useRef, useState } from 'react';
import { getScrollbarWidth, setScrollbarPlaceholder } from '@/utils';
import tailwindConfig from '../../tailwind.screens';

declare global {
  interface Window {
    lenis?: Lenis;
  }
}

export const SmoothScroller = ({ main = true, ...props }) => {
  const lenisRef = useRef<Lenis>(undefined);
  const rafHandleRef = useRef<number>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [disableScroll, setDisableScroll] = useState(false);
  const scrollbarWidth = useRef<number>(-1);

  useEffect(() => {
    const _setIsDesktop = () => {
      setIsDesktop(window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10));
    };

    _setIsDesktop();
    watchDisableScroll();

    window.addEventListener('resize', _setIsDesktop);
    return () => window.removeEventListener('resize', _setIsDesktop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (disableScroll) {
      if (lenisRef.current) {
        lenisRef.current?.stop();
      } else {
        document.documentElement.classList.add('lenis');
        document.documentElement.classList.add('lenis-stopped');
      }

      if (scrollbarWidth.current === -1) {
        scrollbarWidth.current = getScrollbarWidth();
      }
      if (scrollbarWidth.current > 0) {
        setScrollbarPlaceholder(scrollbarWidth.current);
      }
    } else {
      if (lenisRef.current) {
        lenisRef.current?.start();
      } else {
        document.documentElement.classList.remove('lenis');
        document.documentElement.classList.remove('lenis-stopped');
      }

      if (scrollbarWidth.current > 0) {
        setScrollbarPlaceholder(0);
      }
      scrollbarWidth.current = -1;
    }
  }, [disableScroll]);

  useEffect(() => {
    if (!isDesktop) return;

    // Initialize Lenis on the first render
    if (!lenisRef.current) {
      lenisRef.current = new Lenis(props);

      if (typeof window !== 'undefined' && main) {
        window.lenis = lenisRef.current;
      }

      const raf = (time: number) => {
        lenisRef.current?.raf(time);
        rafHandleRef.current = requestAnimationFrame(raf);
      };
      rafHandleRef.current = requestAnimationFrame(raf);
    }

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        lenisRef.current?.resize();
      }, 100);
    });
    resizeObserver.observe(document.body);

    // Clean up on component unmount
    return () => {
      resizeObserver.disconnect();
      if (resizeTimeout) clearTimeout(resizeTimeout);
      if (lenisRef.current) {
        lenisRef.current.destroy();
        lenisRef.current = undefined;
      }
      if (rafHandleRef.current) {
        cancelAnimationFrame(rafHandleRef.current);
        rafHandleRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  const watchDisableScroll = () => {
    setDisableScroll(document.getElementsByClassName('disable-scroll').length > 0);

    setTimeout(() => {
      watchDisableScroll();
    }, 200);
  };

  return null;
};

export default SmoothScroller;
